import * as React from 'react';
import isEqual from 'lodash/isEqual';
import Tag from '../../Tag/Tag';
import Icon from '../../Icon/Icon';
import { Button } from '../../Button/Button';
import { ToolbarWrapper } from './ToolbarWrapper';
import { FilterStateManager, FilterType, Filters, FilterSpecification } from '../types';

type ActiveFiltersListProps<T extends FilterType> = {
  manager: FilterStateManager<T>;
  filters: Filters<T>;
  resetFiltersLabel?: string;
};

/**
 * This function attempts to generate a default label for a filter tag,
 * based on the filter's title and current state.
 * @param id - The filter ID.
 * @param filter - The filter specification.
 * @param state - The current state of the filter.
 * @returns A string representing the default label for the filter tag.
 */
const getFilterTagLabel = <T extends FilterType>(id: keyof T, filter: FilterSpecification<T>, state: T[keyof T]) => {
  // If the filter has a custom label function, use it
  if (filter.label) {
    return filter.label(state);
  }
  const name = filter.title || String(id);

  // If the filter value is an array...
  if (Array.isArray(state)) {
    // If we have more than one element in the array, show the count
    if (state.length > 1) {
      return `${name} (${state.length})`;
    }
    // React can't render objects, so we stringify
    if (typeof state[0] === 'object') {
      return JSON.stringify(state[0]);
    }
    return state[0];
  }

  if (state && typeof state === 'object') {
    return `${name} ${JSON.stringify(state)}`;
  }

  return `${name}: ${state}`;
};

const ActiveFilterList = <T extends FilterType>({ manager, filters, resetFiltersLabel }: ActiveFiltersListProps<T>) => {
  const activeFilters = (Object.keys(manager.state.filterValues) as Array<keyof T>)
    .filter(id => !isEqual(manager.state.filterValues[id], filters[id].initialValue))
    .sort((a, b) => {
      if (filters[a].required === filters[b].required) {
        return 0;
      }
      return filters[a].required ? -1 : 1;
    });

  return (
    <ToolbarWrapper
      aria-label="Active filters"
      key={activeFilters.length}
      more={({ count, from }) => (
        <>
          {from !== null && (
            <Tag
              aria-label={`${count} additional filters`}
              onClick={() => manager.actions.showFilter(activeFilters[from])}
            >
              +{count}
            </Tag>
          )}
          {manager.actions.hasActiveFilters() && (
            <Button type="transparent" aria-label="Reset filters" size="small" onClick={manager.actions.clearFilters}>
              {resetFiltersLabel}
            </Button>
          )}
        </>
      )}
    >
      {() =>
        activeFilters.map(id => {
          const filter = filters[id];
          const state = manager.state.filterValues[id];

          return (
            <Tag
              key={id as string}
              aria-label={`"${filter.title}" settings`}
              onClick={() => manager.actions.showFilter(id)}
              action={
                filter.required
                  ? undefined
                  : () => (
                      <Tag.Button
                        aria-label={`Clear "${filters[id].title || String(id)}"`}
                        onClick={() => manager.actions.resetFilter(id)}
                      >
                        <Icon type="close" size="md" />
                      </Tag.Button>
                    )
              }
            >
              {getFilterTagLabel(id, filter, state)}
            </Tag>
          );
        })
      }
    </ToolbarWrapper>
  );
};

export default ActiveFilterList;
